import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import type {
  MemoryFact,
  MemoryEpisode,
  MemoryPlaybookEntry,
  MemoryCommitment,
  SuppressionRule,
} from '../types';

interface UseMemoryInsightsResult {
  facts: MemoryFact[];
  episodes: MemoryEpisode[];
  playbook: MemoryPlaybookEntry[];
  commitments: MemoryCommitment[];
  suppressionRules: SuppressionRule[];
  loading: boolean;
}

export function useMemoryInsights(userId: string | null): UseMemoryInsightsResult {
  const [facts, setFacts] = useState<MemoryFact[]>([]);
  const [episodes, setEpisodes] = useState<MemoryEpisode[]>([]);
  const [playbook, setPlaybook] = useState<MemoryPlaybookEntry[]>([]);
  const [commitments, setCommitments] = useState<MemoryCommitment[]>([]);
  const [suppressionRules, setSuppressionRules] = useState<SuppressionRule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setFacts([]);
      setEpisodes([]);
      setPlaybook([]);
      setCommitments([]);
      setSuppressionRules([]);
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    let resolvedCount = 0;
    const totalSubscriptions = 5;
    const markResolved = () => {
      resolvedCount += 1;
      if (resolvedCount >= totalSubscriptions) {
        setLoading(false);
      }
    };

    const unsubFacts = onSnapshot(
      query(collection(db, 'users', userId, 'memory', 'facts', 'items'), orderBy('updatedAt', 'desc')),
      (snap) => {
        setFacts(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as MemoryFact)));
        markResolved();
      },
      () => markResolved()
    );
    const unsubEpisodes = onSnapshot(
      query(collection(db, 'users', userId, 'memory', 'episodes', 'items'), orderBy('updatedAt', 'desc')),
      (snap) => {
        setEpisodes(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as MemoryEpisode)));
        markResolved();
      },
      () => markResolved()
    );
    const unsubPlaybook = onSnapshot(
      query(collection(db, 'users', userId, 'memory', 'playbook', 'items'), orderBy('updatedAt', 'desc')),
      (snap) => {
        setPlaybook(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as MemoryPlaybookEntry)));
        markResolved();
      },
      () => markResolved()
    );
    const unsubCommitments = onSnapshot(
      query(collection(db, 'users', userId, 'memory', 'commitments', 'items'), orderBy('updatedAt', 'desc')),
      (snap) => {
        setCommitments(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as MemoryCommitment)));
        markResolved();
      },
      () => markResolved()
    );
    const unsubSuppression = onSnapshot(
      query(collection(db, 'users', userId, 'memory', 'suppressionRules', 'items'), orderBy('updatedAt', 'desc')),
      (snap) => {
        setSuppressionRules(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as SuppressionRule)));
        markResolved();
      },
      () => markResolved()
    );

    return () => {
      unsubFacts();
      unsubEpisodes();
      unsubPlaybook();
      unsubCommitments();
      unsubSuppression();
    };
  }, [userId]);

  return {
    facts,
    episodes,
    playbook,
    commitments,
    suppressionRules,
    loading,
  };
}
